import {
  getNonRegistrantLicenseMapPinsOptions,
  getNonRegistrantLicensesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  exportNonRegistrantLicenses,
  getNonRegistrantLicenseFilterValues,
} from "@/hera-api/client/sdk.gen";
import { regionQueryParams } from "@/hera-api/regions-api";
import { dashboardListQueryOptions } from "@/lib/dashboard-query-options";
import {
  BACKGROUND_QUERY_META,
  isForegroundFetch,
} from "@/lib/foreground-fetch";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import type { MapCluster, MapViewport } from "@/lib/map-clusters";
import { trackServerRequest } from "@/lib/server-request-activity";
import type { NonRegistrantLicenseListLicenseResponseSchema } from "@/types";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { usePrefetchAdjacentListPages } from "./use-prefetch-adjacent-list-pages";

export type LicenseListSortDir = "asc" | "desc";

export function serializeLicenseListFilters(
  filters: Record<string, string[]>,
): string | undefined {
  const cleaned: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(filters)) {
    if (values && values.length > 0) cleaned[key] = values;
  }
  if (Object.keys(cleaned).length === 0) return undefined;
  return JSON.stringify(cleaned);
}

export function usePaginatedNonRegistrantLicenses({
  jurisdictionIds,
  licenseTypeIds,
  selectedRegionIds,
  page,
  pageSize,
  sortBy,
  sortDir,
  columnFilters,
  enabled,
  useCancellationReasonAsStatus = false,
}: {
  jurisdictionIds: string[];
  licenseTypeIds?: string[];
  selectedRegionIds?: string[];
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: LicenseListSortDir;
  columnFilters: Record<string, string[]>;
  enabled: boolean;
  useCancellationReasonAsStatus?: boolean;
}) {
  const filtersJson = serializeLicenseListFilters(columnFilters);
  const query = useMemo(
    () => ({
      jurisdiction_ids:
        jurisdictionIds.length > 0 ? jurisdictionIds : undefined,
      license_type_ids:
        licenseTypeIds && licenseTypeIds.length > 0
          ? licenseTypeIds
          : undefined,
      ...regionQueryParams(selectedRegionIds),
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
      filters: filtersJson,
      use_cancellation_reason_as_status: useCancellationReasonAsStatus,
    }),
    [
      jurisdictionIds,
      licenseTypeIds,
      selectedRegionIds,
      page,
      pageSize,
      sortBy,
      sortDir,
      filtersJson,
      useCancellationReasonAsStatus,
    ],
  );

  const queryClient = useQueryClient();
  const result = useQuery({
    ...getNonRegistrantLicensesOptions({ query }),
    ...dashboardListQueryOptions,
    enabled,
    placeholderData: keepPreviousData,
  });

  const prefetchPage = useCallback(
    (adjacentPage: number) => {
      void queryClient.prefetchQuery({
        ...getNonRegistrantLicensesOptions({
          query: { ...query, page: adjacentPage },
        }),
        ...dashboardListQueryOptions,
        meta: BACKGROUND_QUERY_META,
      });
    },
    [query, queryClient],
  );

  usePrefetchAdjacentListPages({
    enabled,
    isFetching: result.isFetching,
    page,
    pageSize,
    totalCount: result.data?.total_count ?? 0,
    prefetchPage,
  });

  const licenses = (result.data?.licenses ??
    []) as NonRegistrantLicenseListLicenseResponseSchema[];

  useEffect(() => {
    if (!enabled) return;
    setLicenseDisplayedSnapshot({
      pageLicenseIds: licenses.map((row) => row.id),
      pageJlts: licenses.map((row) => ({
        licenseId: row.id,
        jurisdictionId: row.jurisdiction.id,
        licenseTypeId: row.license_type.id,
        registrantAccountId: null,
      })),
      headerJurisdictionIds: jurisdictionIds,
      headerLicenseTypeIds: licenseTypeIds ?? [],
    });
  }, [enabled, licenses, jurisdictionIds, licenseTypeIds]);

  return {
    licenses,
    totalCount: result.data?.total_count ?? 0,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
    error: result.error,
    dataUpdatedAt: result.dataUpdatedAt,
    refetch: result.refetch,
    listQuery: query,
  };
}

export function useNonRegistrantLicenseMapPins({
  jurisdictionIds,
  licenseTypeIds,
  selectedRegionIds,
  columnFilters,
  enabled,
  useCancellationReasonAsStatus = false,
  viewport = null,
}: {
  jurisdictionIds: string[];
  licenseTypeIds?: string[];
  selectedRegionIds?: string[];
  columnFilters: Record<string, string[]>;
  enabled: boolean;
  useCancellationReasonAsStatus?: boolean;
  viewport?: MapViewport | null;
}) {
  const filtersJson = serializeLicenseListFilters(columnFilters);
  const query = useMemo(
    () => ({
      jurisdiction_ids:
        jurisdictionIds.length > 0 ? jurisdictionIds : undefined,
      license_type_ids:
        licenseTypeIds && licenseTypeIds.length > 0
          ? licenseTypeIds
          : undefined,
      ...regionQueryParams(selectedRegionIds),
      filters: filtersJson,
      use_cancellation_reason_as_status: useCancellationReasonAsStatus,
      north: viewport?.north,
      south: viewport?.south,
      east: viewport?.east,
      west: viewport?.west,
      zoom: viewport?.zoom,
    }),
    [
      jurisdictionIds,
      licenseTypeIds,
      selectedRegionIds,
      filtersJson,
      useCancellationReasonAsStatus,
      viewport,
    ],
  );

  const result = useQuery({
    ...getNonRegistrantLicenseMapPinsOptions({ query }),
    enabled,
    placeholderData: keepPreviousData,
  });

  const pins = result.data?.pins ?? [];
  const clusters = (result.data?.clusters ?? []) as MapCluster[];
  const mode = result.data?.mode ?? "pins";

  useEffect(() => {
    if (!enabled) {
      setLicenseDisplayedSnapshot({
        pinLicenseIds: [],
        pinJlts: [],
        mapIsClustered: false,
      });
      return;
    }
    setLicenseDisplayedSnapshot({
      pinLicenseIds: mode === "pins" ? pins.map((pin) => pin.id) : [],
      pinJlts:
        mode === "pins"
          ? pins.map((pin) => ({
              licenseId: pin.id,
              jurisdictionId: pin.jurisdiction_id,
              licenseTypeId: pin.license_type_id,
            }))
          : [],
      mapIsClustered: mode === "clusters",
    });
  }, [enabled, mode, pins]);

  return {
    pins,
    clusters,
    mode,
    totalInView: result.data?.total_in_view ?? pins.length,
    totalMatched: result.data?.total_matched ?? pins.length,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
    error: result.error,
    refetch: result.refetch,
  };
}

export async function fetchNonRegistrantLicenseFilterValues(args: {
  column: string;
  jurisdictionIds: string[];
  licenseTypeIds?: string[];
  selectedRegionIds?: string[];
  columnFilters: Record<string, string[]>;
  useCancellationReasonAsStatus?: boolean;
  search?: string;
}): Promise<string[]> {
  const q = args.search?.trim() || undefined;
  const { data } = await trackServerRequest(() =>
    getNonRegistrantLicenseFilterValues({
      query: {
        column: args.column,
        jurisdiction_ids:
          args.jurisdictionIds.length > 0 ? args.jurisdictionIds : undefined,
        license_type_ids:
          args.licenseTypeIds && args.licenseTypeIds.length > 0
            ? args.licenseTypeIds
            : undefined,
        ...regionQueryParams(args.selectedRegionIds),
        filters: serializeLicenseListFilters(args.columnFilters),
        use_cancellation_reason_as_status: args.useCancellationReasonAsStatus,
        q,
      },
    }),
  );
  return data?.values ?? [];
}

export async function fetchNonRegistrantLicenseExport(args: {
  jurisdictionIds: string[];
  licenseTypeIds?: string[];
  selectedRegionIds?: string[];
  columnFilters: Record<string, string[]>;
  sortBy: string;
  sortDir: LicenseListSortDir;
  useCancellationReasonAsStatus?: boolean;
}): Promise<NonRegistrantLicenseListLicenseResponseSchema[]> {
  const { data } = await trackServerRequest(() =>
    exportNonRegistrantLicenses({
      query: {
        jurisdiction_ids:
          args.jurisdictionIds.length > 0 ? args.jurisdictionIds : undefined,
        license_type_ids:
          args.licenseTypeIds && args.licenseTypeIds.length > 0
            ? args.licenseTypeIds
            : undefined,
        ...regionQueryParams(args.selectedRegionIds),
        filters: serializeLicenseListFilters(args.columnFilters),
        sort_by: args.sortBy,
        sort_dir: args.sortDir,
        use_cancellation_reason_as_status: args.useCancellationReasonAsStatus,
      },
    }),
  );
  return (data?.licenses ??
    []) as NonRegistrantLicenseListLicenseResponseSchema[];
}
